// =============================================================================
// DropdownMenu Component
// =============================================================================
// A compound dropdown menu built with native HTML. No external dependencies.
// Uses a click-to-open pattern with focus management and keyboard navigation.
//
// The dropdown content uses fixed positioning so it is never clipped by
// overflow:hidden ancestors.
//
// Usage:
//   <DropdownMenu>
//     <DropdownTrigger>
//       <button>Open</button>
//     </DropdownTrigger>
//     <DropdownContent>
//       <DropdownLabel>Account</DropdownLabel>
//       <DropdownItem onClick={...}>Profile</DropdownItem>
//       <DropdownSeparator />
//       <DropdownItem danger onClick={...}>Logout</DropdownItem>
//     </DropdownContent>
//   </DropdownMenu>
// =============================================================================

import {
  type ReactNode,
  type HTMLAttributes,
  useState,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect,
  createContext,
  useContext,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface DropdownCtx {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  align: DropdownAlign;
}

const DropdownContext = createContext<DropdownCtx>({
  isOpen: false,
  toggle: () => {},
  close: () => {},
  triggerRef: { current: null },
  align: 'right',
});

/**
 * Access the enclosing dropdown's `close()` from custom (non-DropdownItem)
 * content — e.g. chip buttons that trigger a filter and should dismiss the
 * panel.
 */
export function useDropdownMenuClose(): () => void {
  return useContext(DropdownContext).close;
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export type DropdownAlign = 'left' | 'right';

export interface DropdownMenuProps {
  children: ReactNode;
  /** Alignment of the panel relative to the trigger. @default 'right' */
  align?: DropdownAlign;
}

export function DropdownMenu({ children, align = 'right' }: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((o) => !o), []);

  // Close on outside click and Escape
  useEffect(() => {
    if (!isOpen) return;

    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      // Ignore clicks on the trigger or inside the container
      if (containerRef.current?.contains(target)) return;
      // Check portal content via data attribute
      const portalEl = document.querySelector('[data-dropdown-portal]');
      if (portalEl?.contains(target)) return;
      setIsOpen(false);
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <DropdownContext.Provider value={{ isOpen, toggle, close, triggerRef, align }}>
      <div ref={containerRef} className="relative inline-block text-left">
        {children}
      </div>
    </DropdownContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Trigger — wraps the clickable element that toggles the menu
// ---------------------------------------------------------------------------

export interface DropdownTriggerProps {
  children: ReactNode;
  className?: string;
}

export function DropdownTrigger({ children, className }: DropdownTriggerProps) {
  const { toggle, isOpen, triggerRef } = useContext(DropdownContext);

  return (
    <button
      ref={triggerRef}
      type="button"
      className={clsx('inline-flex items-center', className)}
      aria-haspopup="true"
      aria-expanded={isOpen}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Content — floating panel rendered via portal with fixed positioning
// ---------------------------------------------------------------------------

export interface DropdownContentProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Override min width. @default 'min-w-[220px]' */
  minWidth?: string;
}

export function DropdownContent({
  children,
  minWidth = 'min-w-[220px]',
  className,
  ...props
}: DropdownContentProps) {
  const { isOpen, triggerRef, align } = useContext(DropdownContext);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; right: number }>({
    top: 0,
    left: 0,
    right: 0,
  });

  // Calculate position based on trigger element
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 8, // 8px gap
      left: rect.left,
      right: window.innerWidth - rect.right,
    });
  }, [isOpen, triggerRef]);

  if (!isOpen) return null;

  const style: React.CSSProperties = {
    position: 'fixed',
    top: position.top,
    zIndex: 9999,
    ...(align === 'right'
      ? { right: position.right }
      : { left: position.left }),
  };

  return createPortal(
    <div
      ref={panelRef}
      data-dropdown-portal
      className={clsx(
        'rounded-lg border border-border bg-surface py-1 shadow-lg',
        minWidth,
        className,
      )}
      style={style}
      role="menu"
      {...props}
    >
      {children}
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Item — individual clickable menu option
// ---------------------------------------------------------------------------

export interface DropdownItemProps extends HTMLAttributes<HTMLButtonElement> {
  /** Icon or element displayed before the label. */
  icon?: ReactNode;
  /** Danger variant (red text). */
  danger?: boolean;
  /** Disable the item. */
  disabled?: boolean;
}

export function DropdownItem({
  icon,
  danger = false,
  disabled = false,
  className,
  children,
  onClick,
  ...props
}: DropdownItemProps) {
  const { close } = useContext(DropdownContext);

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      className={clsx(
        'flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors',
        'text-left',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && !danger && 'text-text-primary hover:bg-surface-secondary',
        !disabled && danger && 'text-danger hover:bg-danger/5',
        className,
      )}
      onClick={(e) => {
        if (disabled) return;
        onClick?.(e);
        close();
      }}
      {...props}
    >
      {icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center">{icon}</span>}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Separator
// ---------------------------------------------------------------------------

export function DropdownSeparator() {
  return <div className="my-1 border-t border-border" role="separator" />;
}

// ---------------------------------------------------------------------------
// Label — non-interactive header inside the panel
// ---------------------------------------------------------------------------

export interface DropdownLabelProps {
  children: ReactNode;
  className?: string;
}

export function DropdownLabel({ children, className }: DropdownLabelProps) {
  return (
    <div className={clsx('px-3 py-2 text-xs font-medium text-text-tertiary', className)}>
      {children}
    </div>
  );
}
